import React, { useState } from "react";
import { BRAND, NEUTRAL, getPaletteColor } from "../../../utils/chartTheme";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Text,
} from "recharts";
import { formatCount } from "../../../utils/numberFormat";

const ConditionChart = ({ data, loading }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-2 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-48 bg-gray-100 rounded"></div>
      </div>
    );
  }

  if (!data || !data.conditionAssessment) {
    return (
      <div className="bg-white rounded-lg shadow">
        <h3 className="text-xl my-2 p-0 text-[#3A3A3A] font-bold uppercase text-balance">
          Physical Condition Assessment
        </h3>
        <div className="text-center py-8 text-gray-500">
          No condition data available
        </div>
      </div>
    );
  }

  const conditionData = Object.entries(data.conditionAssessment).map(
    ([key, value]) => ({
      name: key,
      value: parseFloat(value.percentage),
      count: value.count,
      color: getConditionColor(key),
      shortName: key.replace(" Condition", ""),
      description: getConditionDescription(key),
    })
  );

  function getConditionColor(condition) {
    switch (condition) {
      case "Excellent Condition":
        return BRAND.teal;
      case "Good Condition":
        return BRAND.tealLight;
      case "Fair Condition":
        return BRAND.gold;
      case "Poor Condition":
        return getPaletteColor(4);
      default:
        return NEUTRAL.muted;
    }
  }

  function getConditionDescription(condition) {
    switch (condition) {
      case "Excellent Condition":
        return "Features in like-new condition, requiring no repairs or maintenance";
      case "Good Condition":
        return "Features with minor wear but fully functional";
      case "Fair Condition":
        return "Features showing signs of wear and needing maintenance";
      case "Poor Condition":
        return "Features requiring significant repairs or replacement";
      default:
        return "";
    }
  }

  const totalAssessed = data.totalFeaturesAssessed || 0;
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-300 shadow-xl rounded-lg  max-w-xs">
          <div className="flex items-center mb-3">
            <div
              className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
              style={{ backgroundColor: data.color }}
            ></div>
            <div>
              <h4 className="font-bold text-gray-800 text-lg text-balance">{data.name}</h4>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-2xl font-bold" style={{ color: data.color }}>
                {data.value}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Percentage</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-2xl font-bold text-gray-800">
                {formatCount(data.count)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Features Count</div>
            </div>
          </div>

          <div className="pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Out of total:</span>
              <span className="font-bold text-gray-800">
                {totalAssessed} features
              </span>
            </div>
            <div className="flex justify-between items-center text-sm mt-1">
              <span className="text-gray-600">Ratio:</span>
              <span className="font-bold text-gray-800">
                {formatCount(data.count)}/{totalAssessed}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    name,
    value,
    shortName,
  }) => {

    if (percent < 0.08) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#ffffff"
        textAnchor="middle"
        dominantBaseline="central"
        fontWeight="bold"
        fontSize="12"
      >
        {`${value}%`}
      </text>
    );
  };

  return (
    <div className="bg-white">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800 text-balance">
          Physical Condition Assessment
        </h3>
        {/* <div className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
          Total: {totalAssessed} features
        </div> */}
      </div>
      <div className="flex flex-col items-center">
        <div className="h-80 w-full max-w-2xl">
          <ResponsiveContainer width="100%" height="90%">
            <PieChart>
              <Pie
                data={conditionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                innerRadius={60}
                fill="#8884d8"
                dataKey="value"
                paddingAngle={3}
                onMouseEnter={(data, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
                className="cursor-pointer"
              >
                {conditionData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    stroke={activeIndex === index ? "#ffffff" : entry.color}
                    strokeWidth={activeIndex === index ? 3 : 1}
                    opacity={
                      activeIndex === null || activeIndex === index ? 1 : 0.8
                    }
                    className="transition-all duration-200"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />

              {/* Center Text */}
              {/* <Text
                x="50%"
                y="45%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="16"
                fontWeight="600"
                fill="#374151"
              >
                Total Assessed
              </Text> */}
              {/* <Text
                x="50%"
                y="55%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="28"
                fontWeight="bold"
                fill="#1F2937"
              >
                {totalAssessed}
              </Text> */}
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-full max-w-2xl ">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {conditionData.map((item, index) => (
              <div
                key={index}
                className={`flex flex-col items-center rounded-lg transition-all duration-200 cursor-pointer ${
                  activeIndex === index ? "bg-gray-100 shadow-md" : "bg-gray-50"
                }`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <div className="flex items-center justify-center mb-1">
                  <div
                    className="w-4 h-4 rounded-full mr-2"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="font-medium text-gray-700 text-sm">
                    {item.shortName}
                  </span>
                </div>
                <div className="text-center">
                  <div
                    className="text-2xl font-bold"
                    style={{ color: item.color }}
                  >
                    {item.value}%
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {item.count} features
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConditionChart;
